/**
 * Single colour/material filters canonically live in the URL *path*
 * (`/color-{slug}`, `/material-{slug}`) so each combo page has exactly one
 * indexable URL — see {@link ./filter-path-redirect.ts}.
 *
 * The Rocco URL system, however, keeps colours/materials as query parameters and
 * does not recognise these path segments. Fed the raw path it treats `color-white`
 * as part of the category path, which makes the category lookup resolve to a
 * `NotFoundError` (the directory page 404s) and leaves the active filter pill
 * unselected. This rewrites those SEO path segments back into the query form the
 * Rocco parser understands, leaving the rest of the URL (category path, sort
 * segment, existing query string) untouched.
 */
const PRODUCT_FILTER_PATH_PREFIXES: Readonly<Record<string, string>> = {
    color: 'colors',
    material: 'materials',
};

/**
 * Rewrite `/products/.../color-{slug}` / `/material-{slug}` path segments into the
 * equivalent `?colors=`/`?materials=` query parameters so the Rocco URL parser can
 * understand them. Returns the URL unchanged when there is nothing to rewrite.
 */
export const rewriteProductFilterPathToQuery = (url: URL): URL => {
    if (!url.pathname.startsWith('/products/')) {
        return url;
    }

    const remainingSegments: string[] = [];
    const extractedFilters: Array<[param: string, value: string]> = [];

    for (const segment of url.pathname.split('/')) {
        const separatorIndex = segment.indexOf('-');
        if (separatorIndex > 0) {
            const prefix = segment.slice(0, separatorIndex);
            const value = segment.slice(separatorIndex + 1);
            const param = PRODUCT_FILTER_PATH_PREFIXES[prefix];
            if (param && value) {
                extractedFilters.push([param, value]);
                continue;
            }
        }
        remainingSegments.push(segment);
    }

    if (!extractedFilters.length) {
        return url;
    }

    const rewritten = new URL(url.href);
    rewritten.pathname = remainingSegments.join('/');
    for (const [param, value] of extractedFilters) {
        rewritten.searchParams.append(param, value);
    }
    return rewritten;
};
